import * as React from "react";
import * as ReactDOM from "react-dom";
import "core-js/stable";
import "regenerator-runtime/runtime";
import themes from "./themes/theme-context";
import RouterContext from "./routes/routesMapper";
import { UserContext } from "./userProfile/userProfileContext";
import { AppConfigContext } from "./appConfigContext";
import { MuiThemeProvider } from "@material-ui/core/styles";
import "./index.scss";
import "./styles/rmax.icons.scss";
import "./styles/font-family.scss";
import "./styles/print.scss";
// Do not remove this Interceptor import.
import Interceptor from "./auth/interceptor";
import stores from "./stores/index";
import { Provider } from "mobx-react";
import "@dataGrid/agGridSetup";
import StyledSnackbar from "./StyledSnackbar";
import AuthService from "./auth/AuthService";
import { fetchConfig, getDeserializedRouteNameConfig, getTerminalAccessLabel } from "./utilities/appHelper";
import { getDataWithQueryString } from "./api/componentAPIs";
import { FeatureContext } from "./userProfile/featureFlagsContext";
import appConstants from "./appConstants";
import { updateFreshDesk } from "./utilities/freshDeskUtils";
import Events from "./utilities/EventEmitter";
import memoryLoggerInstance from "./services/MemoryLogger";
import loggerService from "./services/LoggerService/LoggerConfig";
import { RootErrorBoundary as ErrorBoundary } from "./ErrorBoundary/index";
import registerUnhandledRejectionHandler from "./utilities/unhandledRejectionHandler";
import { parseNoFreightConfig } from "./utilities/noFreightConfig";
import { parseInternationalDocumentsConfig } from "./utilities/internationalDocumentsConfig";

class App extends React.Component {
  constructor(props) {
    super(props);
    this.initializeAppConfiguration();
    
    this.state = {
      theme: this.getDefaultTheme(),
      userData: {
        userProfile: null,
        updateUserProfile: this.userProfileUpdate,
        toggleTheme: this.toggleTheme,
      },
      configJSON: null,
      featureFlagData: {
        featureFlags: [],
        setFeatureFlags: this.setFeatureFlags,
        isFeatureEnabled: this.isFeatureEnabled
      },
    };
  }

  componentDidMount() {
    memoryLoggerInstance?.startMonitoringMemory?.();
  }

  componentWillUnmount() {
    memoryLoggerInstance?.stopMonitoringMemory?.();
  }

   userProfileUpdate = ({geoJson, theme, ...profileData}) => {
      const defaultTheme = this.getDefaultTheme(theme || this.state.theme?.themeName);
      this.setState((state) => ({
        userData: {
          ...state.userData,
          userProfile: {
            ...state.userData.userProfile,
            ...profileData,
            theme: defaultTheme.themeName,
            geoJson: geoJson ?? state.userData.userProfile.geoJson,
          },
        },
      }),() => {
         const user = {
           id: this.state.userData.userProfile.userId,
           name: this.state.userData.userProfile.userName,
           terminalCode: this.state.userData.userProfile.terminal.code,
           scenarioID: this.state.userData.userProfile.scenarioID,
         };
        updateFreshDesk(this.state.userData.userProfile)
        loggerService.setUser(user);
      });
      
    };

   setAppConfig = (configJSON) => {
      const noFreightConfigParsed = parseNoFreightConfig(configJSON?.noFreightConfig);
      const internationalDocumentsParsed = parseInternationalDocumentsConfig(
        configJSON?.internationalDocuments
      );
      this.setState({
        configJSON: {
          ...configJSON,
          noFreightConfigParsed,
          internationalDocumentsParsed,
          terminalAccessLabel: getTerminalAccessLabel(
            configJSON.terminalAccessLabelType
          ),
          routeNameEditParams: getDeserializedRouteNameConfig(
            configJSON.routeNameConfig
          ),
        },
      });
      stores.DispatchStore?.setShowReturnReasonCode?.(configJSON?.showReturnReasonCode);
    };


   toggleTheme = (themeName) => {
    const themeObj = this.getDefaultTheme(themeName);
    if (this.state.theme !== themeObj) {
      this.setState({ theme: themeObj });
      this.userProfileUpdate({
        ...this.state.userData.userProfile,
        theme: themeObj.themeName,
      });
    }
  };

  /* "User preferences not available or not matching: Necessary because some parts of the 
  application read themeName from userProfile, while others use Material-UI's withTheme function.
  If the user preference is not present or does not match, the light theme should be shown as the default theme.
  */
  getDefaultTheme = (themeName) => {
    if (!themeName) {
      return themes.modernLight;
    }
    return themes[themeName] || (themeName === 'modernDark' ? themes.modernDark : themes.modernLight);
  };

  setFeatureFlags = (featureFlags) => {
    this.setState((state) => ({
      featureFlagData: {
        ...state.featureFlagData,
        featureFlags,
      },
    }));
    memoryLoggerInstance?.setHighMemoryUsageWarningFeatureFlag?.(
      this.isFeatureEnabled('enableHighMemoryUsageWarning')
    );
  };

  isFeatureEnabled = (featureName) => {
    return Boolean(
      this.state.featureFlagData.featureFlags?.find(
        (feature) => feature.id === featureName
      )?.value
    );
  };

  initializeAppConfiguration = async () => {
    try {
      const configJSON = await fetchConfig();
      
      const responseData = await getDataWithQueryString(
        "GetIBPlanningConfiguration",
        null
      );
      
      let cityDispatchResponse = null;
      try {
        cityDispatchResponse = await getDataWithQueryString(
          "GetDispatchConfiguration",
          null
        );
      } catch (dispatchErr) {
        // Proceed even if GetDispatchConfiguration fails (e.g., 404)
        console.warn("GetDispatchConfiguration failed, proceeding without it:", dispatchErr);
      }

      if (configJSON) {
        loggerService.initializeLogger({ ...configJSON, ...responseData.data });
      }
      if (responseData?.status === 200) {
        this.setAppConfig({ 
          ...configJSON,
          ...responseData.data,
          ...(cityDispatchResponse?.status === 200 ? cityDispatchResponse.data : {})
        });
      }
    } catch (err) {
      Events.emit('NavigateToAccessDenied', appConstants.NAV_URLS.SERVER_ERROR);
    }
  };

  render() {
    return (
      <AppConfigContext.Provider value={this.state.configJSON}>
          <UserContext.Provider value={this.state.userData}>
            <FeatureContext.Provider value={this.state.featureFlagData}>
              <MuiThemeProvider theme={this.state.theme}>
                <StyledSnackbar>
                  <ErrorBoundary>
                    <RouterContext />
                  </ErrorBoundary>
                </StyledSnackbar>
              </MuiThemeProvider>
            </FeatureContext.Provider>
          </UserContext.Provider>
        </AppConfigContext.Provider>
    );
  }
}

const renderApp = () =>
  ReactDOM.render(
    <Provider {...stores}>
      <App />
    </Provider>,
    document.getElementById("root")
  );

Interceptor.configure();
registerUnhandledRejectionHandler();
AuthService.initKeycloak(renderApp);
